import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Clock3,
  CreditCard,
  MessageCircle,
  Package,
  RefreshCcw,
  User,
  XCircle,
} from "lucide-react";
import PublicOrderStoreFooter from "@/components/customer-orders/PublicOrderStoreFooter";
import PublicOrderStoreHeader from "@/components/customer-orders/PublicOrderStoreHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  buildCustomerOrderWhatsAppUrl,
  crmApi,
  extractCrmError,
  getCustomerOrderPublicStatusLabel,
} from "@/lib/crmApi";

function formatMoney(value, currency = "KZT") {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  const formatted = new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value));
  return `${formatted} ${currency}`.trim();
}

function formatDate(value) {
  if (!value) return "—";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("ru-RU", { dateStyle: "medium" });
}

function formatOrderDate(value) {
  if (!value) return "—";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function getHeroStatusBadge(order) {
  if (order.offer_expired || order.public_status === "expired") {
    return { label: "Срок истёк", className: "border-amber-200 bg-amber-50 text-amber-800" };
  }
  if (order.public_status === "confirmed") {
    return { label: "Подтверждён", className: "border-emerald-200 bg-emerald-50 text-emerald-700" };
  }
  if (order.public_status === "declined") {
    return { label: "Отказ", className: "border-red-200 bg-red-50 text-red-700" };
  }
  if (order.public_status === "cancelled") {
    return { label: "Отменён", className: "border-red-200 bg-red-50 text-red-700" };
  }
  if (["ready", "completed", "received"].includes(order.public_status)) {
    const label = getCustomerOrderPublicStatusLabel(order);
    return { label, className: "border-emerald-200 bg-emerald-50 text-emerald-700" };
  }
  if (order.public_status === "awaiting_payment") {
    const label = getCustomerOrderPublicStatusLabel(order);
    return { label, className: "border-amber-200 bg-amber-50 text-amber-800" };
  }
  const label = getCustomerOrderPublicStatusLabel(order);
  return { label, className: "border-blue-200 bg-blue-50 text-blue-700" };
}

function getPaymentStatusLabel(order) {
  if (order.is_fully_paid) return "Оплачен полностью";
  if (Number(order.paid_amount) > 0) return "Частично оплачен";
  return "Не оплачен";
}

function shouldShowPaymentInfo(order) {
  if (!order) return false;
  if (Number(order.paid_amount) > 0 || order.is_fully_paid) return true;
  return !["draft", "sent", "viewed", "awaiting_confirmation", "declined", "cancelled"].includes(order.status);
}

function getItemLineDiscount(item) {
  if (item.discount_amount !== null && item.discount_amount !== undefined) {
    return Number(item.discount_amount) || 0;
  }
  const subtotal = Number(item.subtotal);
  const total = Number(item.total);
  if (!Number.isNaN(subtotal) && !Number.isNaN(total)) {
    return Math.max(0, subtotal - total);
  }
  return 0;
}

function getItemSubtotal(item) {
  if (item.subtotal !== null && item.subtotal !== undefined) {
    return Number(item.subtotal) || 0;
  }
  return Number(item.total) || 0;
}

function OrderItemsSection({ items, currency }) {
  if (!Array.isArray(items) || items.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 p-6 text-center text-sm text-slate-500">
        Позиции в заказе пока не добавлены.
      </div>
    );
  }

  return (
    <>
      {/* Desktop table */}
      <div className="hidden overflow-x-auto rounded-2xl border border-slate-200 md:block">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/90 hover:bg-slate-50/90">
              <TableHead className="w-[110px] font-semibold text-slate-600">Артикул</TableHead>
              <TableHead className="min-w-[180px] font-semibold text-slate-600">Наименование</TableHead>
              <TableHead className="w-[72px] text-right font-semibold text-slate-600">Кол-во</TableHead>
              <TableHead className="w-[130px] text-right font-semibold text-slate-600">Без скидки</TableHead>
              <TableHead className="w-[110px] text-right font-semibold text-slate-600">Скидка</TableHead>
              <TableHead className="w-[130px] text-right font-semibold text-slate-600">Сумма</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => {
              const subtotal = getItemSubtotal(item);
              const discount = getItemLineDiscount(item);
              return (
                <TableRow key={item.id} className="hover:bg-slate-50/60">
                  <TableCell className="font-medium text-slate-800">{item.product_code || "—"}</TableCell>
                  <TableCell className="text-slate-900">{item.product_name}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-700">{item.quantity}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-700">
                    {formatMoney(subtotal, currency)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-slate-700">
                    {formatMoney(discount, currency)}
                  </TableCell>
                  <TableCell className="text-right text-base font-semibold tabular-nums text-slate-950">
                    {formatMoney(item.total, currency)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Mobile cards */}
      <div className="space-y-3 md:hidden">
        {items.map((item) => {
          const subtotal = getItemSubtotal(item);
          const discount = getItemLineDiscount(item);
          return (
            <div key={item.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="font-medium leading-snug text-slate-950">{item.product_name}</p>
              {item.product_code && (
                <p className="mt-1 text-xs text-slate-500">Артикул: {item.product_code}</p>
              )}
              <div className="mt-3 flex items-center justify-between gap-3">
                <span className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700">
                  {item.quantity} шт.
                </span>
              </div>
              <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-3 text-sm">
                <div className="flex items-center justify-between gap-3 text-slate-600">
                  <span>Без скидки</span>
                  <span className="tabular-nums">{formatMoney(subtotal, currency)}</span>
                </div>
                <div className="flex items-center justify-between gap-3 text-slate-600">
                  <span>Скидка</span>
                  <span className="tabular-nums">{formatMoney(discount, currency)}</span>
                </div>
                <div className="flex items-center justify-between gap-3 font-semibold text-slate-950">
                  <span>Сумма</span>
                  <span className="text-lg tabular-nums">{formatMoney(item.total, currency)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function ExpiredAlert({ onRequestPrice, loading, requested }) {
  return (
    <div className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-4 shadow-sm sm:p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <p className="font-semibold text-amber-950">Срок предложения истёк</p>
            <p className="mt-1 text-sm leading-relaxed text-amber-900/90">
              Наличие и цена могли измениться. Запросите актуальное предложение — менеджер свяжется с вами.
            </p>
          </div>
        </div>
        <Button
          className="w-full shrink-0 bg-amber-600 hover:bg-amber-700 sm:w-auto"
          onClick={onRequestPrice}
          disabled={loading || requested}
        >
          <RefreshCcw className="mr-2 h-4 w-4" />
          {loading ? "Отправляем..." : requested ? "Запрос отправлен" : "Запросить актуальную цену"}
        </Button>
      </div>
    </div>
  );
}

function PageSkeleton() {
  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-8 sm:px-6">
      <Skeleton className="h-8 w-3/4 max-w-md rounded-lg" />
      <Skeleton className="h-5 w-40 rounded-md" />
      <Skeleton className="h-24 w-full rounded-2xl" />
      <Skeleton className="h-64 w-full rounded-2xl" />
    </div>
  );
}

export default function CrmCustomerOrderPublicPage() {
  const { publicToken } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [declineLoading, setDeclineLoading] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineComment, setDeclineComment] = useState("");
  const [priceRequestLoading, setPriceRequestLoading] = useState(false);
  const [priceRequested, setPriceRequested] = useState(false);

  const loadOrder = useCallback(async () => {
    if (!publicToken) {
      setError("Ссылка заказа некорректна");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await crmApi.getPublicCustomerOrder(publicToken);
      setOrder(data || null);
    } catch (err) {
      setError(extractCrmError(err, "Заказ по ссылке не найден или недоступен"));
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [publicToken]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const whatsappQuestionUrl = useMemo(() => {
    if (!order?.order_number) return null;
    return buildCustomerOrderWhatsAppUrl(
      order.order_number,
      `Здравствуйте! У меня вопрос по заказу ${order.order_number}.`
    );
  }, [order?.order_number]);

  const handleRequestPrice = async () => {
    if (!publicToken || !order?.can_request_price || priceRequested) return;
    setPriceRequestLoading(true);
    setActionMessage(null);
    try {
      const result = await crmApi.requestPublicCustomerOrderPrice(publicToken);
      setOrder(result.order || order);
      setPriceRequested(true);
      setActionMessage(result.message || "Запрос отправлен. Менеджер скоро пришлёт актуальную цену.");
    } catch (err) {
      setActionMessage(extractCrmError(err, "Не удалось отправить запрос"));
    } finally {
      setPriceRequestLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!publicToken || !order?.can_confirm) return;
    setConfirmLoading(true);
    setActionMessage(null);
    try {
      const result = await crmApi.confirmPublicCustomerOrder(publicToken);
      setOrder(result.order || order);
      setActionMessage(result.message || "Заказ подтверждён. Менеджер скоро примет его в работу.");
    } catch (err) {
      setActionMessage(extractCrmError(err, "Не удалось подтвердить заказ"));
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!publicToken || !order?.can_decline) return;
    setDeclineLoading(true);
    setActionMessage(null);
    try {
      const result = await crmApi.declinePublicCustomerOrder(publicToken, {
        comment: declineComment.trim() || undefined,
      });
      setOrder(result.order || order);
      setActionMessage(result.message || "Мы получили ваш отказ.");
      setDeclineOpen(false);
      setDeclineComment("");
    } catch (err) {
      setActionMessage(extractCrmError(err, "Не удалось отправить отказ"));
    } finally {
      setDeclineLoading(false);
    }
  };

  const heroBadge = order ? getHeroStatusBadge(order) : null;
  const showBottomActions = order && (order.can_confirm || order.can_decline || whatsappQuestionUrl);
  const showPaymentInfo = order && shouldShowPaymentInfo(order);
  const paymentStatusLabel = order ? getPaymentStatusLabel(order) : "";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <PublicOrderStoreHeader />

      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
        {loading && <PageSkeleton />}

        {!loading && error && (
          <Card className="border-red-200 bg-white shadow-sm">
            <CardContent className="flex flex-col items-start gap-4 p-6 sm:flex-row sm:items-center">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 text-red-500" />
                <div>
                  <h2 className="text-lg font-semibold text-slate-950">Не удалось открыть заказ</h2>
                  <p className="mt-1 text-sm text-slate-600">{error}</p>
                </div>
              </div>
              <Button variant="outline" onClick={loadOrder} className="shrink-0">
                <RefreshCcw className="mr-2 h-4 w-4" />
                Повторить
              </Button>
            </CardContent>
          </Card>
        )}

        {!loading && !error && order && (
          <div className="space-y-6">
            {/* Hero */}
            <section className="space-y-3">
              <div className="flex flex-wrap items-start gap-3">
                <h1 className="text-2xl font-bold leading-tight tracking-tight text-slate-950 sm:text-3xl">
                  Заказ покупателя № {order.order_number}
                </h1>
                {heroBadge && (
                  <Badge variant="outline" className={`mt-1 rounded-full px-3 py-1 ${heroBadge.className}`}>
                    {heroBadge.label}
                  </Badge>
                )}
              </div>
              <p className="text-base text-slate-600">от {formatOrderDate(order.created_at)}</p>
            </section>

            {actionMessage && (
              <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                <p>{actionMessage}</p>
              </div>
            )}

            {order.offer_expired && order.can_request_price && (
              <ExpiredAlert
                onRequestPrice={handleRequestPrice}
                loading={priceRequestLoading}
                requested={priceRequested}
              />
            )}

            {/* Main card */}
            <Card className="overflow-hidden border-slate-200/80 bg-white shadow-sm">
              <CardContent className="space-y-6 p-5 sm:p-6">
                {/* Client block */}
                <div className="flex items-start gap-3 rounded-2xl bg-slate-50 px-4 py-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                    <User className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Клиент</p>
                    <p className="mt-0.5 text-base font-medium text-slate-950">
                      {order.client_name_snapshot || "—"}
                    </p>
                    {order.phone_snapshot && (
                      <a
                        href={`tel:${String(order.phone_snapshot).replace(/\s/g, "")}`}
                        className="mt-1 inline-block text-sm text-blue-700 hover:underline"
                      >
                        {order.phone_snapshot}
                      </a>
                    )}
                  </div>
                </div>

                {/* Dates */}
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <CalendarDays className="h-3.5 w-3.5" />
                      Дата создания
                    </div>
                    <p className="mt-2 text-sm font-medium text-slate-950">{formatDate(order.created_at)}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <Clock3 className="h-3.5 w-3.5" />
                      Дата поставки
                    </div>
                    <p className="mt-2 text-sm font-medium text-slate-950">
                      {formatDate(order.promised_delivery_date)}
                    </p>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <div className="mb-4 flex items-center gap-2">
                    <Package className="h-4 w-4 text-blue-600" />
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Состав заказа</h2>
                  </div>
                  <OrderItemsSection items={order.items} currency={order.currency} />
                </div>

                {/* Totals */}
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border border-slate-100 bg-slate-50/80 px-4 py-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Сумма без скидки</p>
                    <p className="mt-2 text-xl font-semibold tabular-nums text-slate-900">
                      {formatMoney(order.subtotal_amount, order.currency)}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-100 bg-slate-50/80 px-4 py-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Скидка</p>
                    <p className="mt-2 text-xl font-semibold tabular-nums text-slate-900">
                      {formatMoney(order.discount_amount, order.currency)}
                    </p>
                  </div>
                  <div className="rounded-2xl border-2 border-[#3566f6]/25 bg-gradient-to-br from-[#f5f8ff] to-white px-4 py-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#174bd7]">Итого к оплате</p>
                    <p className="mt-2 text-2xl font-bold tabular-nums text-[#0b1538] sm:text-3xl">
                      {formatMoney(order.total_amount, order.currency)}
                    </p>
                  </div>
                </div>

                {showPaymentInfo && (
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                      <CreditCard className="h-4 w-4 text-blue-600" />
                      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Оплата</h2>
                      <Badge
                        variant="outline"
                        className={
                          order.is_fully_paid
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : Number(order.paid_amount) > 0
                              ? "border-amber-200 bg-amber-50 text-amber-800"
                              : "border-slate-200 bg-slate-50 text-slate-600"
                        }
                      >
                        {paymentStatusLabel}
                      </Badge>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Оплачено</p>
                        <p className="mt-1 text-lg font-semibold tabular-nums text-emerald-900">
                          {formatMoney(order.paid_amount, order.currency)}
                        </p>
                      </div>
                      <div className="rounded-xl border border-amber-100 bg-amber-50/50 px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Остаток</p>
                        <p className="mt-1 text-lg font-semibold tabular-nums text-amber-900">
                          {formatMoney(order.remaining_amount, order.currency)}
                        </p>
                      </div>
                      <div className="rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Статус заказа</p>
                        <p className="mt-1 text-sm font-medium leading-snug text-slate-900">
                          {getCustomerOrderPublicStatusLabel(order)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {order.customer_comment && (
                  <div className="rounded-2xl bg-slate-50 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Комментарий</p>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">{order.customer_comment}</p>
                  </div>
                )}

                {/* Bottom actions — no duplicate price button when alert is shown */}
                {showBottomActions && (
                  <div className="flex flex-col gap-3 border-t border-slate-100 pt-6 sm:flex-row sm:flex-wrap">
                    {order.can_confirm && (
                      <Button
                        size="lg"
                        className="w-full bg-emerald-600 hover:bg-emerald-700 sm:w-auto"
                        onClick={handleConfirm}
                        disabled={confirmLoading}
                      >
                        <CheckCircle2 className="mr-2 h-5 w-5" />
                        {confirmLoading ? "Подтверждаем..." : "Подтвердить заказ"}
                      </Button>
                    )}

                    {whatsappQuestionUrl && (
                      <Button size="lg" variant="outline" className="w-full sm:w-auto" asChild>
                        <a href={whatsappQuestionUrl} target="_blank" rel="noopener noreferrer">
                          <MessageCircle className="mr-2 h-5 w-5" />
                          Задать вопрос в WhatsApp
                        </a>
                      </Button>
                    )}

                    {order.can_decline && (
                      <Button
                        size="lg"
                        variant="ghost"
                        className="w-full text-slate-600 sm:w-auto"
                        onClick={() => setDeclineOpen(true)}
                        disabled={declineLoading}
                      >
                        <XCircle className="mr-2 h-4 w-4" />
                        Отказаться от заказа
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <PublicOrderStoreFooter />
          </div>
        )}
      </main>

      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Отказ от заказа</DialogTitle>
            <DialogDescription>
              Вы можете кратко указать причину — менеджер увидит комментарий в карточке заказа.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={declineComment}
            onChange={(event) => setDeclineComment(event.target.value)}
            placeholder="Не подходит цена, срок или состав заказа..."
            rows={4}
            maxLength={500}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineOpen(false)} disabled={declineLoading}>
              Отмена
            </Button>
            <Button variant="destructive" onClick={handleDecline} disabled={declineLoading}>
              {declineLoading ? "Отправляем..." : "Отказаться от заказа"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
